/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/**
 * Normalize a hotel name to compare it regardless of case, spacing and apostrophe style
 * @param {String} name - hotel name
 * @returns {String}
 */
const normalizeName = (name) => name.trim().replace(/\s+/g, ' ').replace(/’/g, "'").toLowerCase();

/**
 * Regex source that matches a hotel name regardless of spacing and apostrophe style
 * @param {String} name - hotel name
 * @returns {String}
 */
const toNamePattern = (name) =>
  name
    .trim()
    .split(/\s+/)
    // only the syntax characters are escaped, any other escape is invalid with the unicode flag
    .map((part) => part.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&').replace(/['’]/g, "['’]"))
    .join('\\s+');

/**
 * Create a function that replaces the actual hotel names in a text with their demo mode labels
 * @param {Array} hotelList - hotel dropdown list
 * @param {Boolean} isDemoUser - Whether current user is a demo user
 * @returns {Function} takes a text and returns it with the actual hotel names replaced
 */
const createHotelNameAnonymizer = (hotelList, isDemoUser = false) => {
  const labels = new Map();
  hotelList?.forEach((hotel) => {
    // the actual name of a hotel is kept in altName for the demo users
    const actualName = isDemoUser ? hotel?.altName : hotel?.name;
    const demoLabel = isDemoUser ? hotel?.name : hotel?.altName;
    if (typeof actualName !== 'string' || typeof demoLabel !== 'string') return;
    if (!actualName.trim() || !demoLabel.trim()) return;
    const key = normalizeName(actualName);
    if (key === normalizeName(demoLabel) || labels.has(key)) return;
    labels.set(key, { actualName, demoLabel });
  });
  if (labels.size === 0) return (text) => text;

  // longest names first, so that a name is not replaced partially by a shorter name it contains
  const patterns = [...labels.values()]
    .map(({ actualName }) => actualName)
    .sort((a, b) => b.length - a.length)
    .map(toNamePattern);
  let regex;
  try {
    regex = new RegExp(`(?<![\\p{L}\\p{N}])(?:${patterns.join('|')})(?![\\p{L}\\p{N}])`, 'giu');
  } catch (err) {
    console.error('Failed to create the hotel name anonymizer: ', err?.message);
    return (text) => text;
  }

  return (text) => {
    if (typeof text !== 'string' || !text) return text;
    return text.replace(regex, (match) => labels.get(normalizeName(match))?.demoLabel ?? match);
  };
};

export default createHotelNameAnonymizer;
